import React, { useEffect, useState, useContext } from "react";
import { getSubjects } from "../../api/classSubjectApi";
import { AuthContext } from "../../store/AuthContext";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Container,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
  Breadcrumbs,
  Link
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Home, Class } from "@mui/icons-material";
import StudentLayout from "../../layouts/StudentLayout";

export default function StudentSelectSubject() {
  const { user, token } = useContext(AuthContext);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const theme = useTheme();

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const isTV = useMediaQuery("(min-width: 1920px)");

  const classId = params.get("class_id") || user.class_id;

  // Detect Android WebView / TV Mode
  const isAndroidApp = () =>
    navigator.userAgent.includes("VrPlayApp") ||
    navigator.userAgent.includes("AndroidWebView") ||
    window.AndroidWebView !== undefined;

  const isAndroidTV = () =>
    navigator.userAgent.includes("AndroidTV") ||
    navigator.userAgent.includes("TV") ||
    isAndroidApp();

  useEffect(() => {
    loadSubjects();
  }, [classId]);

  const loadSubjects = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getSubjects(token);
      setSubjects(res.data);
    } catch (err) {
      setError("Failed to load subjects. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-focus first card on TV
  useEffect(() => {
    if (!loading && subjects.length > 0 && isAndroidTV()) {
      setTimeout(() => {
        const firstCard = document.querySelector(".tv-card");
        if (firstCard) firstCard.focus();
      }, 120);
    }
  }, [loading, subjects]);

  // TV DPAD Navigation
  useEffect(() => {
    if (!isAndroidTV()) return;

    const handleDPAD = (e) => {
      const cards = Array.from(document.querySelectorAll(".tv-card"));
      if (cards.length === 0) return;

      const currentIndex = cards.indexOf(document.activeElement);
      const columns = isTV ? 4 : isDesktop ? 3 : isTablet ? 2 : 1;

      let nextIndex = currentIndex;

      switch (e.key) {
        case "ArrowRight":
        case "Right":
          nextIndex = currentIndex + 1;
          break;

        case "ArrowLeft":
        case "Left":
          nextIndex = currentIndex - 1;
          break;

        case "ArrowDown":
        case "Down":
          nextIndex = currentIndex + columns;
          break;

        case "ArrowUp":
        case "Up":
          nextIndex = currentIndex - columns;
          break;

        case "Enter":
        case "NumpadEnter":
        case " ":
          document.activeElement?.click();
          e.preventDefault();
          return;

        default:
          return;
      }

      if (nextIndex >= 0 && nextIndex < cards.length) {
        cards[nextIndex].focus();
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", handleDPAD);

    return () => window.removeEventListener("keydown", handleDPAD);
  }, [subjects, isTV, isDesktop, isTablet]);

  // TV-enhanced, responsive card styles
  const getCardStyles = () => {
    const base = {
      cursor: "pointer",
      overflow: "hidden",
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
      }
    };

    if (isTV) {
      return {
        ...base,
        height: 280,
        borderRadius: 3,
        transition: "all 0.2s ease",
        "&:focus": {
          transform: "scale(1.08)",
          boxShadow: `0 0 30px ${theme.palette.primary.main}`,
          border: `3px solid ${theme.palette.primary.main}`,
          outline: "none"
        }
      };
    }

    if (isDesktop) {
      return {
        ...base,
        height: 220,
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: theme.shadows[6]
        }
      };
    }

    if (isTablet) {
      return {
        ...base,
        height: 180
      };
    }

    return {
      ...base,
      height: 140
    };
  };

  const getGridConfig = () => {
    if (isTV) return { xs: 12, sm: 6, md: 4, lg: 3 };
    if (isDesktop) return { xs: 12, sm: 6, md: 4 };
    if (isTablet) return { xs: 12, sm: 6 };
    return { xs: 12 };
  };

  const openVideos = (subjectId) => {
    navigate(`/student/select-topic?class_id=${classId}&subject_id=${subjectId}`);
  };

  if (loading) {
    return (
      <Box textAlign="center" py={6}>
        <CircularProgress size={isTV ? 80 : 50} />
        <Typography mt={2}>Loading Subjects...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
        <Button onClick={loadSubjects}>Retry</Button>
      </Alert>
    );
  }

  return (
    <StudentLayout>
      <Container maxWidth="xl" sx={{ py: 5 }}>
        
        {/* Hide header if inside Android App */}
        {!isAndroidApp() && (
          <Box mb={4}>
            <Typography variant={isTV ? "h2" : "h3"} fontWeight="bold">
              Select Your Subject
            </Typography>
            <Typography color="text.secondary" mt={1}>
              Explore subjects and continue learning
            </Typography>
          </Box>
        )}

        <Grid container spacing={isTV ? 4 : 3}>
          {subjects.map((sub) => (
            <Grid item {...getGridConfig()} key={sub.id}>
              <Card
                className="tv-card"
                tabIndex={0}
                onClick={() => openVideos(sub.id)}
                sx={getCardStyles()}
              >
                <CardContent
                  sx={{
                    textAlign: "center",
                    p: isTV ? 4 : 3
                  }}
                >
                  <Box
                    sx={{
                      width: isTV ? 90 : 60,
                      height: isTV ? 90 : 60,
                      borderRadius: "50%",
                      mb: 2,
                      fontSize: isTV ? "2.5rem" : "1.5rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      color: "#fff",
                      fontWeight: "bold"
                    }}
                  >
                    {sub.subject?.charAt(0)}
                  </Box>

                  <Typography
                    variant={isTV ? "h3" : "h5"}
                    fontWeight="bold"
                    color="text.primary"
                  >
                    {sub.subject}
                  </Typography>

                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

      </Container>
    </StudentLayout>
  );
}
